/**
 * 「作業係の仕事」のカード（ADR-0049、D7 の (a): 開いたら出る＋開いている間の更新）。
 * 承認待ちのカードの下に出す。仕事が無ければ出さない。動いている仕事がある間だけ、設定の間隔（config/worker.yaml の card.refresh_sec）で
 * 描き直す（画面が隠れている間は描き直さない）。取り消しのボタンは「頼む」だけ（止めるのは作業係）。
 * 声の端末・未適用の環境では出ない（Gateway が 403・503 を返す）。
 */
import { ApiError, api } from "./api.js";
import { canCancel, type JobCardItem, type JobsView, jobsCardModel } from "./jobs-model.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  // 文字列は Text ノードとして入れる（題・本人の言葉を HTML として解釈しない）
  e.append(...children);
  return e;
}

const timers = new WeakMap<HTMLElement, number>();

export async function renderJobsCard(
  root: HTMLElement,
  opts: {
    /** 読めたとき、仕事と作業係の様子を知らせる（引き出しの数の札に使う。ADR-0055） */
    onView?: (v: JobsView) => void;
  } = {},
): Promise<void> {
  const prev = timers.get(root);
  if (prev !== undefined) clearTimeout(prev);
  let view: JobsView;
  try {
    view = await api<JobsView>("/v1/jobs");
  } catch (e) {
    if (!(e instanceof ApiError)) console.warn(e);
    root.replaceChildren();
    return;
  }
  opts.onView?.(view);
  const m = jobsCardModel(view);
  if (!m.show) {
    root.replaceChildren();
    return;
  }
  const status = el("p", { class: "muted small" });
  const row = (j: JobCardItem) => {
    const lines: (Node | string)[] = [
      el(
        "p",
        { class: "job-head" },
        el("span", {}, j.title),
        el("span", { class: `jv-latin job-state ${j.status}` }, j.status_label),
      ),
      // 進みの棒（ADR-0055）。進み具合の数は無いので、動いている間は流れる棒、終われば満ちた棒（数を作らない）
      el(
        "div",
        { class: `job-bar ${j.status}`, "aria-hidden": "true" },
        el("div", { class: "fill" }),
      ),
      el("p", { class: "small muted" }, `${j.kind}・${j.created_at} に受け付け`),
    ];
    if (j.last_event)
      lines.push(el("p", { class: "small" }, `${j.last_event.at} ${j.last_event.summary}`));
    if (j.result) lines.push(el("p", { class: "small" }, j.result));
    if (j.branch) lines.push(el("p", { class: "small mono" }, `成果の置き場: ${j.branch}`));
    if (canCancel(j)) {
      const btn = el("button", {}, "取り消す") as HTMLButtonElement;
      btn.onclick = async () => {
        btn.disabled = true;
        try {
          await api("/v1/jobs/cancel", { job_id: j.job_id });
          await renderJobsCard(root, opts);
        } catch (e) {
          const code = e instanceof ApiError ? e.code : String(e);
          const msg: Record<string, string> = {
            frozen: "今は止まっています（凍結中）。作業係も仕事を中断しています。",
            passkey_session_required: "この端末からは取り消せません。",
          };
          status.textContent = msg[code] ?? `取り消せませんでした（${code}）`;
          btn.disabled = false;
        }
      };
      lines.push(el("div", { class: "row" }, btn));
    }
    return el("div", { class: `job-item ${j.status}` }, ...lines);
  };
  const finished = m.finished.length
    ? el(
        "details",
        { class: "closed" },
        el("summary", {}, `最近終わった仕事（${m.finished.length}）`),
        ...m.finished.map(row),
      )
    : "";
  root.replaceChildren(
    el(
      "section",
      { class: "card jobs" },
      el("h2", {}, "作業係の仕事"),
      ...m.active.map(row),
      finished,
      el("p", { class: "small muted" }, m.worker),
      status,
    ),
  );
  if (m.refreshMs !== null)
    timers.set(
      root,
      window.setTimeout(() => {
        if (document.visibilityState === "visible") void renderJobsCard(root, opts);
        else
          document.addEventListener("visibilitychange", () => void renderJobsCard(root, opts), {
            once: true,
          });
      }, m.refreshMs),
    );
}
